// NotificationSettingsPage.js
import React, { useMemo, useState } from "react";

const notificationTypes = [
    { key: "assignmentGrades", label: "Assignment & Grade Columns" },
    { key: "gradeExtract", label: "Grade Extract" },
    { key: "gradeEvent", label: "Grade Event Notification" },
    { key: "approval", label: "Approval Notification" },
    { key: "invalidGrade", label: "Invalid Grade" },
    { key: "gradeUpdate", label: "Grade Update" }
];

/**
 * Default templates – these act as fallbacks AND as the basis for "Reset".
 * You can override any of these via the `initialTemplates` prop.
 */
const builtInDefaults = {
    assignmentGrades: {
        toAddress: "",
        fromAddress: "noreply@blackboard.com",
        subject: "LearnUAS Grade Journey import process completed on $DATE$",
        bodyTemplate:
            "This is an automated response from the Grade Journey Assignment & Grade Column Import tool which completed on $DATE$./n" +
            "Status: $STATUS$/n" +
            "File transfer message:/n$TRANSFERSTATUS$/n" +
            "Processed row details:/n$ERRORDETAILS$/n"
    },
    gradeExtract: {
        toAddress: "",
        fromAddress: "noreply@blackboard.com",
        subject: "Grade Extract process completed on $DATE$",
        bodyTemplate:
            "This is an automated response from the Grade Journey Grade Extract tool which completed on $DATE$./n" +
            "Status: $STATUS$/n" +
            "File transfer message:/n$TRANSFERSTATUS$/n" +
            "Processed row details:/n$ERRORDETAILS$/n"
    },
    gradeEvent: {
        toAddress: "",
        fromAddress: "noreply@blackboard.com",
        subject: "Grade Event notification on $DATE$",
        bodyTemplate:
            "This is an automated grade event notification generated on $DATE$./n" +
            "Status: $STATUS$/n$ERRORDETAILS$/n"
    },
    approval: {
        toAddress: "",
        fromAddress: "noreply@blackboard.com",
        subject: "Grade Journey approval notification on $DATE$",
        bodyTemplate:
            "This is an automated approval notification generated on $DATE$./n" +
            "Status: $STATUS$/n$ERRORDETAILS$/n"
    },
    invalidGrade: {
        toAddress: "",
        fromAddress: "noreply@blackboard.com",
        subject: "Invalid grade notification on $DATE$",
        bodyTemplate:
            "This is an automated invalid grade notification generated on $DATE$./n" +
            "Status: $STATUS$/n$ERRORDETAILS$/n"
    },
    gradeUpdate: {
        toAddress: "",
        fromAddress: "noreply@blackboard.com",
        subject: "Grade update notification on $DATE$",
        bodyTemplate:
            "This is an automated grade update notification generated on $DATE$./n" +
            "Status: $STATUS$/n$ERRORDETAILS$/n"
    }
};

/**
 * Props:
 *  - initialTemplates?: { [typeKey]: { toAddress, fromAddress, subject, bodyTemplate } }
 *  - onSave?: ({ type, template }) => Promise<void> | void
 */
const NotificationSettingsPage = ({ initialTemplates, onSave }) => {
    // Merge caller defaults with built-ins so you can override per type from the parent
    const defaultTemplates = useMemo(
        () => ({
            ...builtInDefaults,
            ...(initialTemplates || {})
        }),
        [initialTemplates]
    );

    const [selectedType, setSelectedType] = useState("assignmentGrades");
    const [templates, setTemplates] = useState(defaultTemplates);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    const current = templates[selectedType];

    const updateCurrent = (patch) => {
        setTemplates((prev) => ({
            ...prev,
            [selectedType]: { ...prev[selectedType], ...patch }
        }));
    };

    const clearMessages = () => {
        setMessage("");
        setError("");
    };

    const handleSave = async () => {
        clearMessages();
        const template = templates[selectedType];

        try {
            setSaving(true);
            if (onSave) {
                // Save *only* the active notification’s template
                await onSave({ type: selectedType, template });
            } else {
                // Fallback: just log
                console.log("[NotificationSettings] save", selectedType, template);
            }
            setMessage("Notification settings saved for current type.");
        } catch (err) {
            console.error("Save notification settings failed:", err);
            setError("Failed to save notification settings.");
        } finally {
            setSaving(false);
        }
    };

    const handleResetCurrent = () => {
        clearMessages();
        setTemplates((prev) => ({
            ...prev,
            [selectedType]: { ...defaultTemplates[selectedType] }
        }));
        setMessage("Reverted to default template for current type.");
    };

    return (
        <div style={{ padding: "12px 0", fontSize: 13 }}>
            {/* Messages */}
            {message && (
                <div style={{ marginBottom: 8, color: "#125", fontSize: 12 }}>
                    {message}
                </div>
            )}
            {error && (
                <div style={{ marginBottom: 8, color: "#a00", fontSize: 12 }}>
                    {error}
                </div>
            )}

            {/* Settings for (radio group) */}
            <Row
                label="Settings for"
                control={
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                        {notificationTypes.map((t) => (
                            <label key={t.key}>
                                <input
                                    type="radio"
                                    name="notificationType"
                                    value={t.key}
                                    checked={selectedType === t.key}
                                    onChange={() => setSelectedType(t.key)}
                                    style={{ marginRight: 4 }}
                                />
                                {t.label}
                            </label>
                        ))}
                    </div>
                }
            />

            {/* To Address */}
            <Row
                label="To Address"
                control={
                    <>
                        <input
                            style={{ width: 420 }}
                            value={current.toAddress}
                            onChange={(e) =>
                                updateCurrent({ toAddress: e.target.value })
                            }
                        />
                        <RowHelp>
                            Specify the recipients. Multiple addresses should be separated
                            by a comma (,). Leave blank to disable notifications.
                        </RowHelp>
                    </>
                }
            />

            {/* From Address */}
            <Row
                label="From Address"
                control={
                    <>
                        <input
                            style={{ width: 420 }}
                            value={current.fromAddress}
                            onChange={(e) =>
                                updateCurrent({ fromAddress: e.target.value })
                            }
                        />
                        <RowHelp>
                            Specify a single e-mail address that the notification should
                            display as the from address.
                        </RowHelp>
                    </>
                }
            />

            {/* Subject */}
            <Row
                label="Subject"
                control={
                    <>
                        <input
                            style={{ width: 420 }}
                            value={current.subject}
                            onChange={(e) =>
                                updateCurrent({ subject: e.target.value })
                            }
                        />
                        <RowHelp>
                            Enter the subject for the notification. Tokens may be used
                            (see Body Template instructions).
                        </RowHelp>
                    </>
                }
            />

            {/* Body Template */}
            <Row
                label="Body Template"
                control={
                    <>
                        <textarea
                            rows={8}
                            style={{ width: 420 }}
                            value={current.bodyTemplate}
                            onChange={(e) =>
                                updateCurrent({ bodyTemplate: e.target.value })
                            }
                        />
                        <RowHelp>
                            Enter the template for the text of the notification. Tokens
                            may be used to substitute data at delivery time. Valid tokens
                            are: <code>$STATUS$</code> = Process Status;{" "}
                            <code>$DATE$</code> = Date/time job was executed;{" "}
                            <code>$TRANSFERSTATUS$</code> = Status of the file transfer;{" "}
                            <code>$ERRORDETAILS$</code> = Error message for individual
                            rows which were processed; <code>/n</code> = New Line.
                        </RowHelp>
                    </>
                }
            />

            {/* Save / Reset actions – apply only to current type */}
            <div
                style={{
                    marginTop: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 8
                }}
            >
                <button type="button" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving…" : "Save"}
                </button>
                <button type="button" onClick={handleResetCurrent}>
                    Reset to Default
                </button>
                <span style={{ fontSize: 11, opacity: 0.7 }}>
                    Changes apply only to{" "}
                    {
                        notificationTypes.find((t) => t.key === selectedType)
                            ?.label
                    }{" "}
                    notifications.
                </span>
            </div>
        </div>
    );
};

/* ---- layout helpers (same pattern as Incoming/Admin Tools) ---- */

const Row = ({ label, control }) => (
    <div
        style={{
            display: "grid",
            gridTemplateColumns: "220px minmax(200px, 640px)",
            columnGap: 16,
            alignItems: "flex-start",
            marginBottom: 8
        }}
    >
        <div style={{ fontSize: 12, fontWeight: 600, paddingTop: 4 }}>
            {label}
        </div>
        <div>{control}</div>
    </div>
);

const RowHelp = ({ children }) => (
    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>{children}</div>
);

export default NotificationSettingsPage;
